/**
 * Shared types and helpers for CAMPAIGN threshold override UI.
 *
 * Mirrors country-threshold-parts, with one structural difference: a campaign
 * is identified by (provider, id), never the id alone — Voluum ids are UUIDs
 * and Binom ids are small integers, so a bare "1" is ambiguous. Every set/map
 * in here is therefore keyed on the composite.
 */

import type { TagRuleCampaignThreshold } from '@/hooks';
import type { TagCompareTo, TagTrafficProvider } from '@trafficpulse/shared';

export const MAX_CAMPAIGN_THRESHOLD_ROWS = 50;

export const CAMPAIGN_THRESHOLD_HELP =
  'The campaigns listed here are compared against their own value — this beats a country override for those campaigns.';

/** A campaign as the picker sees it. */
export interface CampaignOption {
  id: string;
  name: string;
  provider: TagTrafficProvider;
  countryName?: string;
}

export interface CampaignThresholdUIGroup {
  id: string;
  campaigns: Array<{ provider: TagTrafficProvider; campaignId: string }>;
  value: number;
  /** When set, this row COMPARES instead of pinning `value`. */
  compareTo?: TagCompareTo;
}

/** Rows merge into one UI group only when the whole threshold matches —
 *  the number AND the comparison (or its absence). */
const sameThreshold = (
  a: { value: number; compareTo?: TagCompareTo },
  b: { value: number; compareTo?: TagCompareTo },
): boolean =>
  a.value === b.value &&
  JSON.stringify(a.compareTo ?? null) === JSON.stringify(b.compareTo ?? null);

export function campaignKey(provider: TagTrafficProvider, campaignId: string): string {
  return `${provider}:${campaignId.trim().toLowerCase()}`;
}

const sameCampaign = (
  a: { provider: TagTrafficProvider; campaignId: string },
  b: { provider: TagTrafficProvider; campaignId: string },
) => campaignKey(a.provider, a.campaignId) === campaignKey(b.provider, b.campaignId);

/**
 * Build UI groups with stable ids from consecutive runs of the same numeric
 * value, so editing a threshold does not churn React keys.
 */
export function rowsToUIGroups(
  rows: TagRuleCampaignThreshold[],
): CampaignThresholdUIGroup[] {
  const filled = rows.filter((r) => r.campaignId?.trim());
  const blocks: CampaignThresholdUIGroup[] = [];
  let seq = 0;
  for (const r of filled) {
    const entry = { provider: r.provider, campaignId: r.campaignId.trim() };
    const prev = blocks[blocks.length - 1];
    if (prev && sameThreshold(prev, r)) {
      if (!prev.campaigns.some((c) => sameCampaign(c, entry))) prev.campaigns.push(entry);
    } else {
      blocks.push({
        id: `cg-${seq++}`,
        campaigns: [entry],
        value: r.value,
        ...(r.compareTo ? { compareTo: r.compareTo } : {}),
      });
    }
  }
  const drafts: CampaignThresholdUIGroup[] = [];
  rows.forEach((r, idx) => {
    if (!r.campaignId?.trim()) {
      drafts.push({
        id: `cdraft-${idx}`,
        campaigns: [],
        value: r.value,
        ...(r.compareTo ? { compareTo: r.compareTo } : {}),
      });
    }
  });
  return [...blocks, ...drafts];
}

export function flattenUIGroups(
  groups: CampaignThresholdUIGroup[],
): TagRuleCampaignThreshold[] {
  const out: TagRuleCampaignThreshold[] = [];
  for (const g of groups) {
    const extra = g.compareTo ? { compareTo: g.compareTo } : {};
    if (g.campaigns.length === 0) {
      // Draft row: keeps the value while the user picks campaigns. Stripped on submit.
      out.push({ provider: 'VOLUUM', campaignId: '', value: g.value, ...extra });
    } else {
      for (const c of g.campaigns) {
        out.push({
          provider: c.provider,
          campaignId: c.campaignId,
          value: g.value,
          ...extra,
        });
      }
    }
  }
  return out;
}

export function countAssignedCampaigns(rows: TagRuleCampaignThreshold[]): number {
  return rows.filter((r) => r.campaignId?.trim()).length;
}

/** A campaign may sit in only one group, so hide the ones other groups took. */
export function campaignsAvailableForGroup(
  all: CampaignOption[],
  groups: CampaignThresholdUIGroup[],
  groupId: string,
): CampaignOption[] {
  const current = groups.find((g) => g.id === groupId);
  const mine = new Set(
    current?.campaigns.map((c) => campaignKey(c.provider, c.campaignId)) ?? [],
  );
  const taken = new Set<string>();
  for (const g of groups) {
    if (g.id === groupId) continue;
    for (const c of g.campaigns) taken.add(campaignKey(c.provider, c.campaignId));
  }
  return all.filter((c) => {
    const k = campaignKey(c.provider, c.id);
    return !taken.has(k) || mine.has(k);
  });
}

export function othersCampaignCount(
  groups: CampaignThresholdUIGroup[],
  groupId: string,
): number {
  return groups
    .filter((g) => g.id !== groupId)
    .reduce((n, g) => n + g.campaigns.length, 0);
}

export function CampaignThresholdTableHead() {
  return (
    <>
      <p className="px-4 pt-2 text-[11px] leading-snug text-muted-foreground">
        {CAMPAIGN_THRESHOLD_HELP}
      </p>
      <div className="hidden border-b border-border/40 bg-muted/20 sm:grid sm:grid-cols-[minmax(0,1fr)_5.5rem_2.5rem] sm:gap-3 sm:px-4 sm:py-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Campaigns
        </span>
        <span className="text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Value
        </span>
        <span className="sr-only">Actions</span>
      </div>
    </>
  );
}
